/**
 * fetchSessionBytes — single helper used by every inline editor primitive
 * (Docx, Xlsx, PDFViewer, future PPTX preview) to download the bytes
 * pointed at by ``EditSession.read_url``.
 *
 * Why a helper instead of raw ``fetch``:
 *   - The BE-issued ``read_url`` is sometimes RELATIVE
 *     (``/api/v1/documents/{id}/file``) and sometimes ABSOLUTE
 *     (presigned MinIO URLs from legacy paths). Raw ``fetch`` in a Tauri
 *     WebView resolves relative paths against the ``tauri://localhost``
 *     custom protocol, which 404s the request — every editor would then
 *     try to parse the resulting HTML error page as DOCX/XLSX/PDF and
 *     throw a misleading "central directory not found" / "invalid PDF"
 *     error. We prepend ``window.__ANDO_RUNTIME__?.apiBase`` (set at app
 *     boot for desktop builds) when the URL is relative; absolute URLs
 *     (http/https) — and in-browser ``blob:``/``data:`` sources — pass
 *     through unchanged.
 *
 * Zero-dep on ``@ando/core`` per the leaf-package rule. The runtime
 * config bag is read directly off ``window``.
 *
 * Credentials: ``include`` so the JWT cookie travels with the request
 * (required by the canonical ``/api/v1/documents/{id}/file`` proxy).
 * Presigned MinIO URLs ignore the cookie — no harm in sending it.
 */

const TAURI_FALLBACK_API_URL = "http://localhost:8100/api/v1";

/**
 * Read the runtime API base WITHOUT importing ``@ando/core`` (leaf-
 * package rule). ``window.__ANDO_RUNTIME__`` is the runtime-config bag
 * the docker frontend container injects at boot; ``@ando/core``
 * declares the canonical shape and we duck-type the field here so the
 * editors module stays dep-free. Falls back to the Tauri-local
 * backend host when no runtime config is present + the WebView is
 * Tauri (desktop release bundle has no runtime-config.js server).
 */
function resolveApiBase(): string {
  if (typeof window === "undefined") return "";
  const runtime = (window as { __ANDO_RUNTIME__?: { apiUrl?: string } }).__ANDO_RUNTIME__;
  const runtimeBase = runtime?.apiUrl;
  if (runtimeBase) return runtimeBase;
  const isTauri = "__TAURI_INTERNALS__" in window;
  return isTauri ? TAURI_FALLBACK_API_URL : "";
}

/**
 * Resolve a possibly-relative ``read_url`` into an absolute one safe to
 * pass to ``fetch`` from any WebView. Absolute URLs (http/https) are
 * returned unchanged; relative URLs are prefixed with the runtime API
 * base (or the Tauri fallback). ``/api/v1/...`` URLs are handled
 * specially so we don't double up the prefix when the base already
 * ends in ``/api/v1``.
 */
export function resolveSessionUrl(readUrl: string): string {
  if (/^https?:\/\//i.test(readUrl)) return readUrl;
  // ``blob:`` / ``data:`` read_urls are ALREADY fully-qualified in-browser
  // sources (e.g. the run review station wraps proxy-fetched bytes in an
  // object URL, then hands the viewers a ``blob:`` URL). Fetch them AS-IS:
  // the http(s) test above misses them (scheme isn't ``http``), so without
  // this they'd get the API base prepended → ``…/api/v1blob:http://…`` which
  // 404s → the viewer silently shows "no document". Format-agnostic: every
  // inline viewer (PDF, image, …) funnels through here.
  if (/^(?:blob|data):/i.test(readUrl)) return readUrl;
  const base = resolveApiBase();
  if (!base) return readUrl;
  // Console routes are mounted at the API origin's /platform root, not below
  // /api/v1. Appending them to the product base produces
  // /api/v1/platform/console/... and the PDF preview gets a 403.
  if (readUrl.startsWith("/platform/")) {
    if (/^https?:\/\//i.test(base)) {
      return `${new URL(base).origin}${readUrl}`;
    }
    return readUrl; // Same-origin web proxy (base is /api/v1).
  }
  // Strip the ``/api/v1`` prefix from a relative URL when the base
  // already includes it — otherwise the result is ``…/api/v1/api/v1/…``.
  if (base.endsWith("/api/v1") && readUrl.startsWith("/api/v1/")) {
    return `${base}${readUrl.slice("/api/v1".length)}`;
  }
  // No trailing slash in base; readUrl always starts with one.
  return `${base.replace(/\/$/, "")}${readUrl}`;
}

/**
 * Read the Bearer access token, if any, from the runtime auth bridge.
 *
 * The host app (``apps/client/src/main.tsx``) is expected to expose
 * ``window.__ANDO_AUTH__.getAuthToken: () => Promise<string|null>``
 * at boot — typically wired to ``apiClient.getAuthToken()``. We read
 * it through ``window`` so this module stays zero-dep on
 * ``@ando/core`` (leaf-package rule); in environments where the
 * bridge isn't installed (web BFF flow, tests, Storybook) we silently
 * fall back to cookie-only auth.
 *
 * The Tauri desktop flow REQUIRES this header: ``ando_session`` is a
 * ``SameSite=Lax`` cookie and won't ride along on cross-origin
 * fetches from ``tauri://localhost`` to ``http://localhost:8100``.
 * Without the Bearer header every editor open returns 401.
 */
async function readAuthToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const bridge = (
    window as { __ANDO_AUTH__?: { getAuthToken?: () => Promise<string | null> | string | null } }
  ).__ANDO_AUTH__;
  if (!bridge?.getAuthToken) return null;
  try {
    return (await bridge.getAuthToken()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Fetch the bytes at ``read_url`` as an ``ArrayBuffer``. Throws on
 * non-2xx response so the caller can surface the HTTP status to the
 * user instead of silently feeding error HTML into the editor parser.
 *
 * Auth contract: ``credentials: include`` (web BFF cookie path) PLUS
 * ``Authorization: Bearer …`` when the desktop bridge provides one.
 * Headers passed via ``init.headers`` take precedence so callers can
 * override the auth on a per-request basis (rare).
 */
export async function fetchSessionBytes(readUrl: string, init?: RequestInit): Promise<ArrayBuffer> {
  const resolved = resolveSessionUrl(readUrl);
  const headers = new Headers(init?.headers);
  if (!headers.has("Authorization")) {
    const token = await readAuthToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  const res = await fetch(resolved, {
    credentials: "include",
    ...init,
    headers,
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`);
  }
  return res.arrayBuffer();
}
